import React, { useEffect, useRef, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthImage } from '../hooks/useAuthImage';
import type { PremiumJobCondition } from '../types/api';

interface HeatmapOverlayProps {
    imagePath: string;
    analysis: PremiumJobCondition[];
    activeConditions: string[];
    scale?: number;
    enableBreathing?: boolean;
}

const EASE = [0.22, 0.61, 0.36, 1] as const;

export function FaceHeatmap({ imagePath, analysis, activeConditions, scale: _scale = 1, enableBreathing = false }: HeatmapOverlayProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
    const { src: imageSrc } = useAuthImage(imagePath);

    useEffect(() => {
        const handleResize = () => {
            if (imageRef.current) {
                const { offsetWidth, offsetHeight } = imageRef.current;
                setCanvasSize(prev => {
                    if (prev.width === offsetWidth && prev.height === offsetHeight) return prev;
                    return { width: offsetWidth, height: offsetHeight };
                });
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => { window.removeEventListener('resize', handleResize); };
    }, [imagePath]);

    const hasActive = activeConditions.length > 0;

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full select-none flex items-center justify-center rounded-[22px] overflow-hidden"
            style={{
                boxShadow: hasActive
                    ? '0 0 0 1.5px rgba(42,164,166,0.3), var(--sh-lifted)'
                    : 'var(--sh-lifted)',
                transition: 'box-shadow var(--t-base) var(--ease)',
            }}
        >
            <img
                ref={imageRef}
                src={imageSrc ?? undefined}
                alt="Analysis Base"
                className="max-w-full max-h-full object-contain block relative z-0"
                onLoad={() => {
                    if (imageRef.current) {
                        setCanvasSize({
                            width: imageRef.current.offsetWidth,
                            height: imageRef.current.offsetHeight,
                        });
                    }
                }}
            />

            {canvasSize.width > 0 && (
                <div
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none"
                    style={{ width: canvasSize.width, height: canvasSize.height }}
                >
                    <AnimatePresence>
                        {analysis.map((condition) => {
                            if (!activeConditions.includes(condition.condition_name)) return null;
                            return (
                                <HeatmapLayer
                                    key={condition.condition_name}
                                    condition={condition}
                                    width={canvasSize.width}
                                    height={canvasSize.height}
                                    enableBreathing={enableBreathing}
                                />
                            );
                        })}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
}

function HeatmapLayer({ condition, width, height, enableBreathing }: {
    condition: PremiumJobCondition;
    width: number;
    height: number;
    enableBreathing: boolean;
}) {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, width, height);
        const color = getConditionColor(condition.condition_name);
        const rgb = hexToRgb(color.hex);
        if (!rgb) return;
        const baseColor = `${rgb.r}, ${rgb.g}, ${rgb.b}`;

        for (const loc of condition.locations) {
            const y_min = loc.box_2d[0];
            const x_min = loc.box_2d[1];
            const y_max = loc.box_2d[2];
            const x_max = loc.box_2d[3];

            const x = (x_min / 1000) * width;
            const y = (y_min / 1000) * height;
            const w = ((x_max - x_min) / 1000) * width;
            const h = ((y_max - y_min) / 1000) * height;

            const centerX = x + w / 2;
            const centerY = y + h / 2;
            const radius = Math.max(w, h) * 0.8;

            const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
            ctx.globalCompositeOperation = 'screen';
            gradient.addColorStop(0, `rgba(${baseColor}, 0.65)`);
            gradient.addColorStop(0.4, `rgba(${baseColor}, 0.22)`);
            gradient.addColorStop(1, `rgba(${baseColor}, 0)`);

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.fill();
        }
    }, [condition, width, height]);

    const staggerDelay = useMemo(() => {
        if (!enableBreathing) return 0;
        let hash = 0;
        for (let i = 0; i < condition.condition_name.length; i++) {
            hash = ((hash << 5) - hash) + condition.condition_name.charCodeAt(i);
            hash = hash & hash;
        }
        return Math.abs(hash % 10) * 0.15;
    }, [condition.condition_name, enableBreathing]);

    return (
        <motion.canvas
            ref={canvasRef}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={enableBreathing ? {
                opacity: [0.72, 1, 0.72],
                scale: 1,
            } : {
                opacity: 1,
                scale: 1,
            }}
            exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.24, ease: EASE } }}
            transition={enableBreathing ? {
                opacity: { duration: 4, repeat: Infinity, ease: 'easeInOut', delay: staggerDelay },
                scale: { duration: 0.4, ease: EASE },
            } : {
                duration: 0.4,
                ease: EASE,
            }}
            width={width}
            height={height}
            className="absolute inset-0 w-full h-full"
        />
    );
}

function hexToRgb(hex: string) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: Number.parseInt(result[1], 16),
        g: Number.parseInt(result[2], 16),
        b: Number.parseInt(result[3], 16),
    } : null;
}

/* Condition heatmap colors — semantic domain palette, canvas-only (CSS vars can't reach canvas) */
const CONDITION_HEX: Record<string, string> = {
    acne:      '#c0392b',
    wrinkle:   '#3730a3',
    pigment:   '#c2410c',
    eye:       '#7c3aed',
    hydration: '#0369a1',
    redness:   '#be185d',
    texture:   '#a16207',
    default:   '#1A6F70',
};

function getConditionColor(name: string) {
    const n = name.toLowerCase();
    if (n.includes('acne') || n.includes('blemish') || n.includes('pimple'))                        return { hex: CONDITION_HEX.acne };
    if (n.includes('wrinkle') || n.includes('line') || n.includes('aging'))                         return { hex: CONDITION_HEX.wrinkle };
    if (n.includes('pigment') || n.includes('spot') || n.includes('sun'))  return { hex: CONDITION_HEX.pigment };
    if (n.includes('circle') || n.includes('eye'))                                                   return { hex: CONDITION_HEX.eye };
    if (n.includes('hydrat') || n.includes('dry') || n.includes('oil'))                             return { hex: CONDITION_HEX.hydration };
    if (n.includes('redness'))                                              return { hex: CONDITION_HEX.redness };
    if (n.includes('texture') || n.includes('rough') || n.includes('pore'))                         return { hex: CONDITION_HEX.texture };
    return { hex: CONDITION_HEX.default };
}
